import { useCallback, useEffect, useState } from 'react';

import type { Shortcut } from '@/lib/macKeys';
import { askShell, type QuickSendCommand, type QuickSendState, type ShortcutTarget } from '@/lib/quickSend';

/**
 * The Mac shell's quick-send settings as the settings sheet shows them. The shell holds the truth (the shortcut is
 * registered with the system there); this reads it when the sheet opens and again when the window comes back to
 * the front, since Accessibility is switched in System Settings.
 */
export function useQuickSendSettings() {
  const [state, setState] = useState<QuickSendState | null>(null);

  const send = useCallback(async (command: QuickSendCommand): Promise<void> => {
    const next = await askShell(command);
    if (next !== null) setState(next);
  }, []);

  useEffect(() => {
    let mounted = true;
    const refresh = (): void => {
      void askShell({ action: 'state' }).then((next) => {
        if (mounted && next !== null) setState(next);
      });
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => {
      mounted = false;
      window.removeEventListener('focus', refresh);
    };
  }, []);

  // stable, so an effect that uses them does not start over on every render
  const setEnabled = useCallback(
    (target: ShortcutTarget, enabled: boolean, shortcut: Shortcut): void => void send({ action: 'set', target, enabled, shortcut }),
    [send],
  );
  /** While the shortcut is being recorded the shell ignores the old one, so confirming it does not send. */
  const setRecording = useCallback((on: boolean): void => void send({ action: 'recording', on }), [send]);

  return {
    state,
    setEnabled,
    setRecording,
    allowAccessibility: (): void => void send({ action: 'accessibility' }),
    setLogin: (on: boolean): void => void send({ action: 'login', on }),
    checkUpdates: (): void => void send({ action: 'checkUpdates' }),
  };
}
